// @ts-nocheck
// Sheet pra admin editar o livro atual do clube/grupo:
// - capa (URL ou upload futuro)
// - total de páginas
// - página inicial sincronizada (todos os membros começam aqui)
// - título e autor (caso queira corrigir)
import React, { useEffect, useState } from 'react';
import { Icon } from '../icons';
import { Sheet } from '../common';

export interface EditClubBookValues {
  title: string;
  author: string;
  totalPages: number;
  startPage: number;
  coverUrl: string | null;
  coverHue?: number;
  buyUrl?: string | null;
}

export const EditClubBookSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  initial: EditClubBookValues | null;
  term: 'clube' | 'grupo';
  onSave: (patch: Partial<EditClubBookValues>) => Promise<void>;
}> = ({ open, onClose, initial, term, onSave }) => {
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [totalPages, setTotalPages] = useState('');
  const [startPage, setStartPage] = useState('1');
  const [coverUrl, setCoverUrl] = useState('');
  const [coverHue, setCoverHue] = useState(22);
  const [buyUrl, setBuyUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !initial) return;
    setTitle(initial.title || '');
    setAuthor(initial.author || '');
    setTotalPages(String(initial.totalPages || ''));
    setStartPage(String(initial.startPage || 1));
    setCoverUrl(initial.coverUrl || '');
    setCoverHue(initial.coverHue ?? 22);
    setBuyUrl(initial.buyUrl || '');
    setError(null);
  }, [open, initial]);

  const handleSave = async () => {
    setError(null);
    const totalNum = parseInt(totalPages, 10);
    const startNum = parseInt(startPage, 10);
    if (!title.trim()) return setError('Título obrigatório');
    if (!author.trim()) return setError('Autor obrigatório');
    if (!Number.isFinite(totalNum) || totalNum < 1) return setError('Total de páginas inválido');
    if (!Number.isFinite(startNum) || startNum < 1) return setError('Página inicial inválida');
    if (startNum > totalNum) return setError('Página inicial não pode ser maior que o total');
    const buyTrim = buyUrl.trim();
    if (buyTrim && !/^https?:\/\//i.test(buyTrim)) return setError('O link de compra precisa começar com http:// ou https://');

    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        author: author.trim(),
        totalPages: totalNum,
        startPage: startNum,
        coverUrl: coverUrl.trim() || null,
        coverHue,
        buyUrl: buyTrim || null,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '12px 14px', borderRadius: 12,
    border: '1px solid var(--line)',
    background: 'var(--paper)',
    fontSize: 14, color: 'var(--ink)', outline: 'none',
    fontFamily: 'inherit',
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: 'Geist Mono, monospace',
    fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase',
    color: 'var(--ink-3)', fontWeight: 600, marginTop: 14, marginBottom: 6,
    display: 'block',
  };

  return (
    <Sheet open={open} onClose={onClose} title={`Editar livro do ${term}`}>
      <div style={{ paddingBottom: 8 }}>
        <label style={labelStyle}>Título</label>
        <input value={title} onChange={e => setTitle(e.target.value)} style={fieldStyle} autoFocus={false}/>

        <label style={labelStyle}>Autor</label>
        <input value={author} onChange={e => setAuthor(e.target.value)} style={fieldStyle}/>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>Total de páginas</label>
            <input type="number" inputMode="numeric" min={1} max={500000}
              value={totalPages} onChange={e => setTotalPages(e.target.value)} style={fieldStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Página inicial</label>
            <input type="number" inputMode="numeric" min={1}
              value={startPage} onChange={e => setStartPage(e.target.value)} style={fieldStyle}/>
            <div className="mono" style={{ fontSize: 9, color: 'var(--ink-4)', marginTop: 4, letterSpacing: '0.06em' }}>
              todos começam aqui
            </div>
          </div>
        </div>

        <label style={labelStyle}>URL da capa (opcional)</label>
        <input value={coverUrl} onChange={e => setCoverUrl(e.target.value)} placeholder="https://..." style={fieldStyle}/>
        <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-4)', marginTop: 6, letterSpacing: '0.04em' }}>
          Cole o link de uma imagem. Upload direto vem em breve.
        </div>

        <label style={labelStyle}>Tom da capa (sem URL)</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 6 }}>
          {[10, 30, 60, 140, 220, 320].map(hue => {
            const sel = coverHue === hue;
            return (
              <button key={hue} onClick={() => setCoverHue(hue)} type="button" style={{
                height: 40, borderRadius: 10,
                background: `linear-gradient(135deg, oklch(0.78 0.09 ${hue}), oklch(0.45 0.10 ${hue}))`,
                border: sel ? '2px solid var(--ember)' : '1px solid var(--line)',
                boxShadow: sel ? '0 0 0 2px var(--paper-2), 0 4px 10px -2px rgba(0,0,0,0.2)' : 'none',
              }}/>
            );
          })}
        </div>

        <label style={labelStyle}>Link de compra (opcional)</label>
        <input value={buyUrl} onChange={e => setBuyUrl(e.target.value)} placeholder="https://..." style={fieldStyle} inputMode="url"/>
        <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-4)', marginTop: 6, letterSpacing: '0.04em' }}>
          Onde o membro compra este livro (loja ou seu link de afiliado). Aparece como “adquira aqui” pra quem ainda não tem o livro.
        </div>

        {error && (
          <div style={{
            marginTop: 12, padding: '10px 12px', borderRadius: 10,
            background: 'var(--ember-soft)', color: 'var(--ember)',
            fontSize: 12, fontWeight: 500,
          }}>{error}</div>
        )}

        <button onClick={handleSave} disabled={saving} style={{
          width: '100%', marginTop: 18, padding: '14px', borderRadius: 14,
          background: saving ? 'var(--paper-3)' : 'var(--ember)',
          color: saving ? 'var(--ink-4)' : 'var(--paper)',
          fontSize: 15, fontWeight: 500,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          boxShadow: saving ? 'none' : '0 12px 24px -10px rgba(199,83,26,0.55)',
        }}>
          {saving ? 'Salvando…' : 'Salvar'}
          {!saving && <Icon name="check" size={15} color="var(--paper)" stroke={2.4}/>}
        </button>
      </div>
    </Sheet>
  );
};
